/**
 * ユーザーラベルの2段目に重ねるアイコンの画像を作る。
 *
 * 表示項目は9種類あり、それぞれ別のアイコンを持つ。
 * 地図ライブラリのアイコンは「1枚の画像＋位置の対応表」で渡す決まりなので、
 * ユーザーリストの詳細カードが使っているのと同じSVGを1枚にまとめる。
 *
 * 画像は表示項目の数だけで、ユーザーの件数や値とは無関係なので**1度だけ作る**。
 */

import battery from '@/assets/ifield/ic_battery.svg?raw'
import building from '@/assets/ifield/ic_building.svg?raw'
import checkin from '@/assets/ifield/ic_checkin.svg?raw'
import operatingStatus from '@/assets/ifield/ic_operating-status.svg?raw'
import place from '@/assets/ifield/ic_place.svg?raw'
import positioningAccuracy from '@/assets/ifield/ic_positioning-accuracy.svg?raw'
import positioningTime from '@/assets/ifield/ic_positioning-time.svg?raw'
import sensor from '@/assets/ifield/ic_sensor.svg?raw'
import status from '@/assets/ifield/ic_status.svg?raw'

/**
 * アイコン名とSVGの対応
 * 名前は `UserLabelItemText.js` の `ITEM_ICON` と合わせる
 * @type {Object.<String, String>}
 */
const ICON_SVG = {
  'battery': battery,
  'building': building,
  'checkin': checkin,
  'operating-status': operatingStatus,
  'place': place,
  'positioning-accuracy': positioningAccuracy,
  'positioning-time': positioningTime,
  'sensor': sensor,
  'status': status,
}

/**
 * 使えるアイコン名
 * `UserLabelItemText.js` の `ITEM_ICON` と一致していないと、
 * アイコンが出ないだけで何のエラーも出ないため、テストで突き合わせる
 * @type {Array.<String>}
 */
export const ICON_NAMES = Object.keys(ICON_SVG)

/**
 * アイコン1つの大きさ（SVGの原寸）
 * @type {Number}
 */
const ICON_SIZE = 16

/**
 * 画像を作るときの拡大率（縮小して描くので粗さが出ないように）
 * @type {Number}
 */
const IMAGE_SCALE = 4

/**
 * 作った画像（初回だけ作る）
 * @type {Promise.<Object>|null}
 */
let atlasPromise = null

/**
 * SVGの文字列を画像として読み込む
 * @param {String} svg SVGの文字列
 * @returns {Promise.<Image>} 画像
 */
const loadSvg = (svg) =>
  new Promise((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
  })

/**
 * アイコンを1枚の画像にまとめる
 * @returns {Promise.<Object>} { atlas, mapping }
 */
const build = async () => {
  const names = Object.keys(ICON_SVG)
  const cell = ICON_SIZE * IMAGE_SCALE

  const canvas = document.createElement('canvas')
  canvas.width = cell * names.length
  canvas.height = cell
  const context = canvas.getContext('2d')

  const images = await Promise.all(names.map((name) => loadSvg(ICON_SVG[name])))

  const mapping = {}
  names.forEach((name, index) => {
    const x = cell * index
    context.drawImage(images[index], x, 0, cell, cell)
    mapping[name] = {
      x,
      y: 0,
      width: cell,
      height: cell,
      // 描画時の色指定(getIconColor)を効かせるために必要
      mask: true,
    }
  })

  return { atlas: canvas, mapping }
}

/**
 * アイコンの画像を返す（2回目以降は作り直さない）
 * @returns {Promise.<Object>} { atlas, mapping }
 */
export const loadUserLabelIconAtlas = () => {
  if (!atlasPromise) {
    atlasPromise = build().catch((error) => {
      // 作り直せるように、失敗したら保持しない
      atlasPromise = null
      throw error
    })
  }
  return atlasPromise
}
